import { supabase } from "@/integrations/supabase/client";

export interface QuestionResult {
  questionId: string;
  questionType: string;
  answer: string;
  score: number; // 0 or 1
  points: number;
  // Answer key data, returned by the server at scoring time. Review screens
  // read these instead of the question store, because the learner facing
  // question views deliberately hide the correct pairing / grouping / order.
  correctAnswer?: string;
  correctAnswers?: string[];
  optionExplanations?: Record<string, string>;
  generalExplanation?: string;
  matchPairs?: { left: string; right: string }[];
  categories?: { name: string; items: string | string[] }[];
  orderItems?: { id: string; text: string }[];
  // Learner flagged this question for review during the session.
  flagged?: boolean;
}


export interface PracticeSession {
  id: string;
  userId: string;
  certificationId: string;
  chapterId: string;
  scorePercent: number;
  pointsEarned: number;
  totalPoints: number;
  questionsAttempted: number;
  questionsTotal: number;
  questionsCorrect: number;
  isWeak: boolean;
  durationSeconds: number;
  questionResults: QuestionResult[];
  createdAt: string;
}

type Listener = () => void;
let _items: PracticeSession[] = [];
let _loaded = false;
let _currentUserId: string | null = null;
const listeners = new Set<Listener>();
const notify = () => listeners.forEach((fn) => fn());

function mapRow(r: any): PracticeSession {
  return {
    id: r.id,
    userId: r.user_id,
    certificationId: r.certification_id,
    chapterId: r.chapter_id,
    scorePercent: r.score_percent,
    pointsEarned: r.points_earned,
    totalPoints: r.total_points,
    questionsAttempted: r.questions_attempted,
    questionsTotal: r.questions_total,
    questionsCorrect: r.questions_correct,
    isWeak: r.is_weak,
    durationSeconds: r.duration_seconds,
    questionResults: (r.question_results as QuestionResult[]) || [],
    createdAt: r.created_at,
  };
}

async function loadFromDb(userId: string) {
  if (!userId) return;
  _currentUserId = userId;
  const { data, error } = await supabase
    .from("practice_sessions")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) {
    console.error("Failed to load practice sessions:", error);
    return;
  }
  _items = (data || []).map(mapRow);
  _loaded = true;
  notify();
}

export const practiceSessionStore = {
  get: () => _items,
  subscribe: (fn: Listener) => {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  },
  isLoaded: () => _loaded,

  async loadForUser(userId: string) {
    await loadFromDb(userId);
  },

  async insert(session: Omit<PracticeSession, "id" | "createdAt">): Promise<string | null> {
    const { data, error } = await supabase
      .from("practice_sessions")
      .insert({
        user_id: session.userId,
        certification_id: session.certificationId,
        chapter_id: session.chapterId,
        score_percent: session.scorePercent,
        points_earned: session.pointsEarned,
        total_points: session.totalPoints,
        questions_attempted: session.questionsAttempted,
        questions_total: session.questionsTotal,
        questions_correct: session.questionsCorrect,
        is_weak: session.isWeak,
        duration_seconds: session.durationSeconds,
        question_results: session.questionResults as any,
      })
      .select()
      .single();

    if (error) {
      console.error("Failed to insert practice session:", error);
      return null;
    }

    if (data) {
      _items = [mapRow(data), ..._items];
      notify();
      return data.id;
    }
    return null;
  },

  getForChapter(userId: string, chapterId: string): PracticeSession[] {
    return _items.filter((s) => s.userId === userId && s.chapterId === chapterId);
  },

  getForCert(userId: string, certificationId: string): PracticeSession[] {
    return _items.filter((s) => s.userId === userId && s.certificationId === certificationId);
  },

  reset() {
    _items = [];
    _loaded = false;
    _currentUserId = null;
    notify();
  },
};
